import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { autoBindDecorator } from './api.utils'

import { createCustomError, isErrorCodeExist } from '@/shared/api/errorHandler'
import { CommonServiceMock, CommonService } from './services/CommonService'
import { CountriesService, CountriesServiceMock } from './services/CountriesService'
import { QuoteService, QuoteServiceMock } from './services/QuoteService'
import { TravelersService, TravelersServiceMock } from './services/TravelersService'
import { UserService } from './services/UserService'
import { useApiError } from '@/shared/composables/useApiError/useApiError'
import { PRODUCT_CODE_KEY } from '@/shared/constants/storage.constants'

class ApiErrors {
  static NO_NAME_ERROR = 'No name error'
  static NETWORK_ERROR = 'Network error'
}

export const apiBuilder = (config: AxiosRequestConfig) => {
  const http: AxiosInstance = axios.create(config)

  const onFulfilled = function (response: AxiosResponse) {
    if (response.data && response.data.success !== undefined && !response.data.success) {
      useApiError(response)
      if (isErrorCodeExist(response.data.errorMessage)) {
        return Promise.reject(createCustomError(response.data.errorMessage, { ...response.data }))
      } else {
        return Promise.reject(response.data.errorMessage || ApiErrors.NO_NAME_ERROR)
      }
    }

    const { success, errorMessage, ...payload } = response.data

    if (Object.keys(payload).length === 1) {
      return Object.values(payload)[0]
    }

    return payload
  }

  const onRejected = function (response: any) {
    useApiError(response)

    const data: any = response?.response?.data
    return Promise.reject(data?.errorMessage || ApiErrors.NETWORK_ERROR)
  }

  const beforeSend = (config: InternalAxiosRequestConfig) => {
    if (import.meta.env.MODE !== 'development') {
      const code = localStorage.getItem(PRODUCT_CODE_KEY)
      if (code) {
        config.headers['QIC-Product-Code'] = code
      }
    }
    return config
  }

  http.interceptors.request.use(beforeSend)
  http.interceptors.response.use(onFulfilled, onRejected)

  let withMocks: boolean | undefined = false

  try {
    /*
      Hack:
        import.meta.env is not working in tests
        process.env is not working in development

        So we need this try/catch to avoid errors
     */
    withMocks = import.meta.env.VUE_APP_API_WITH_MOCKS === 'true' || process.env.VITEST === 'true'
  } catch (err) {}

  if (withMocks) {
    return {
      countriesService: autoBindDecorator(new CountriesServiceMock(http)),
      commonService: autoBindDecorator(new CommonServiceMock(http)),
      quoteService: autoBindDecorator(new QuoteServiceMock(http)),
      travelerService: autoBindDecorator(new TravelersServiceMock(http)),
      userService: autoBindDecorator(new UserService(http)),
    }
  } else {
    return {
      countriesService: autoBindDecorator(new CountriesService(http)),
      commonService: autoBindDecorator(new CommonService(http)),
      quoteService: autoBindDecorator(new QuoteService(http)),
      travelerService: autoBindDecorator(new TravelersService(http)),
      userService: autoBindDecorator(new UserService(http)),
    }
  }
}
